import ReactDiffViewer from 'react-diff-viewer';
// LegacyTable is included because there are some questions on how to handle the rowExpansions for this
// The new table doesn't support row expansions, because the general UX pattern is to use Flyouts / Flyins
// But this pattern doesn't match
import { TruncateText } from '@tecton/ComponentRedesign';

import {
  Callout,
  Card,
  Code,
  IconType,
  FlexGroup,
  FlexItem,
  Flyout,
  Link,
  ViewLayout,
  Avatar,
  Tabs,
  Table,
  Text,
  ExtendedTableColumn,
  Button,
  Icon,
  AsymmetricColumnLayout,
  Loading,
  KeyValueList,
  DiffViewer,
} from '@lithos';

import moment from 'moment';
import React, { type FC, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useGetFcos } from '../../../api/fcos';
import { useGetQueryStateUpdateResponse, useGetStateUpdatePlanSummary } from '../../../api/plan-apply-log';
import { FCO, FCOType, WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import { StateUpdatePlanSummaryType } from '../../../feature/plan-summary/PlanSummaryTransform';
import { FcoPropertyRenderingType } from '../../../types/tecton_proto/args/diff_options';
import {
  FcoPropertyDiff,
  StateUpdatePlanSummary,
  StateUpdatePlanSummaryDiff,
} from '../../../types/tecton_proto/data/state_update';
import { TectonDateTimeFormat } from '../../@tecton/ComponentRedesign/utils';
import { useUserSettings } from '../../context/UserSettingsContext';
import processMaterializationInfo from './processMaterializationInfo';
import {
  SingleDiffSummaryLine,
  filterPlanDataByType,
  materializationTypesForFeatureView,
  processDiffItems,
} from './utils';
import { ActionBadge } from './ActionBadge';
import { TableColumnWidth } from '@lithos/Table';

interface FCOTypeTableProps {
  items: StateUpdatePlanSummaryDiff[];
  matchingTypes: string[];
  fcoData: WorkspaceFCOContainer;
}

interface FCOTypeColumn {
  fcoType: string | undefined;
}

const actionColumn = {
  name: 'Action',
  id: 'action',
  content: (item: StateUpdatePlanSummaryDiff) => <ActionBadge type={item.type} />,
  columnWidth: 's' as TableColumnWidth,
};

const nameColumn = (fcoData: Record<string, FCO>, fcoType: FCOType, workspace: string) => {
  const linkToMap: Record<FCOType, string> = {
    [FCOType.DATA_SOURCE]: `../${workspace}/data-sources/`,
    [FCOType.ENTITY]: `../${workspace}/entities/`,
    [FCOType.FEATURE_VIEW]: `../${workspace}/features/`,
    [FCOType.FEATURE_SERVICE]: `../${workspace}/feature-services/`,
    [FCOType.TRANSFORMATION]: `../${workspace}/transformations/`,
    [FCOType.DATASET]: `../${workspace}/datasets/`,
    [FCOType.UNKNOWN]: `../${workspace}/home/`,
  };

  const fcoToIcon: Record<FCOType, IconType> = {
    [FCOType.DATA_SOURCE]: `Sources`,
    [FCOType.ENTITY]: `Entities`,
    [FCOType.FEATURE_VIEW]: `FeatureView`,
    [FCOType.FEATURE_SERVICE]: `FeatureService`,
    [FCOType.TRANSFORMATION]: `Transformations`,
    [FCOType.DATASET]: `Dataset`,
    [FCOType.UNKNOWN]: `Dataset`,
  };

  return {
    name: 'Applied to',
    id: 'applied_to',
    content: (item: StateUpdatePlanSummaryDiff) => {
      const matchingFv = fcoData[item?.name ?? ''];

      if (matchingFv) {
        return <Link url={`${linkToMap[fcoType]}${item.name}`} icon={fcoToIcon[fcoType]} name={item.name} />;
      }
      return <Link disabled icon={fcoToIcon[fcoType]} name={item.name} />;
    },
    columnWidth: 'l' as TableColumnWidth,
  };
};

const changeColumn = {
  name: 'Changes',
  id: 'changes',
  content: (item: StateUpdatePlanSummaryDiff) => <PlanChanges item={item} />,
  columnWidth: 's' as TableColumnWidth,
};

const emptyColumn = {
  name: <></>,
  id: 'empty',
  content: () => <></>,
  columnWidth: 'm' as TableColumnWidth,
};

const PlanChanges: FC<{ item: StateUpdatePlanSummaryDiff }> = ({ item }) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [diffBody, setDiffBody] = useState<React.ReactNode | null>(null);

  const filteredDiffs = item.diffs?.filter(
    (diff) => diff.rendering_type !== FcoPropertyRenderingType.FCO_PROPERTY_RENDERING_TYPE_HIDDEN
  );
  const count = filteredDiffs?.length ?? 0;

  if (!count) {
    return <>n/a</>;
  }

  const columns: ExtendedTableColumn<FcoPropertyDiff & { id: string }>[] = [
    {
      name: 'Property',
      id: 'property',
      columnWidth: 'l' as TableColumnWidth,
      content: (diff: FcoPropertyDiff) => <>{diff.property_name}</>,
    },
    {
      name: 'From',
      id: 'from',
      content: (diff: FcoPropertyDiff) => {
        if (diff.rendering_type === FcoPropertyRenderingType.FCO_PROPERTY_RENDERING_TYPE_PYTHON) {
          // This is where there's a button that sets the diffBody to a DiffViewer
          return (
            <Button
              type="secondary"
              label="View Details"
              onClick={() => {
                setDiffBody(
                  <>
                    <FlexGroup direction="column" gapSize="4" padding="0">
                      <FlexItem grow={0} shrink={0}>
                        <Button
                          type="secondary"
                          label="Go Back"
                          iconLeft="ChevronLeft"
                          onClick={() => {
                            setDiffBody(undefined);
                          }}
                        />
                      </FlexItem>
                      <FlexItem grow={1} shrink={1}>
                        <DiffViewer
                          oldCommit={{
                            code: diff.val_existing ?? '',
                          }}
                          newCommit={{
                            code: diff.val_declared ?? '',
                          }}
                        />
                      </FlexItem>
                    </FlexGroup>
                  </>
                );
              }}
            />
          );
        }

        return <>{diff.val_existing}</>;
      },
    },
    {
      name: 'To',
      id: 'to',
      content: (diff: FcoPropertyDiff) => {
        if (diff.rendering_type === FcoPropertyRenderingType.FCO_PROPERTY_RENDERING_TYPE_PYTHON) {
          return false;
        }

        return <span style={{ textAlign: 'right' }}>{diff.val_declared}</span>;
      },
    },
  ];

  const modalTitle = count === 1 ? 'Property Change' : `${count} Property Changes`;

  return (
    <>
      <Button
        type="ghost"
        label={item.diffs?.length ?? 0}
        iconLeft="Recent"
        onClick={() => {
          setIsFlyoutOpen(true);
        }}
      />
      {isFlyoutOpen && (
        <Flyout
          title={
            <FlexGroup direction="column">
              <FlexItem grow={0} shrink={0}>
                {modalTitle}
              </FlexItem>
              {item.name && (
                <FlexGroup alignItems="center">
                  <Icon icon="FeatureView" size="s" type="mono" />
                  <Text>{item.name}</Text>
                </FlexGroup>
              )}
            </FlexGroup>
          }
          isOpen={isFlyoutOpen}
          setIsOpen={() => {
            setIsFlyoutOpen(!isFlyoutOpen);
          }}
          content={
            <>
              {!diffBody && (
                <Table
                  items={filteredDiffs?.map((diff) => ({ ...diff, id: diff.property_name ?? '' })) ?? []}
                  columns={columns}
                />
              )}
              {diffBody && <>{diffBody}</>}
            </>
          }
        />
      )}
    </>
  );
};

const CHANGES_TYPE_MAP: Record<string, string> = {
  'Feature Table': 'Table',
  'Feature Service': 'Service',
  'Realtime Feature View': 'Realtime',
  'Batch Feature View': 'Batch',
  'Batch Data Source': 'Batch',
  'Stream Feature View': 'Stream',
  'Stream Data Source': 'Stream',
  'Stream Feature View with Push Source': 'Stream with Push Source',
};

const TypeColumn: FC<FCOTypeColumn> = ({ fcoType }) => {
  if (!fcoType) return <>n/a</>;

  const changeType = CHANGES_TYPE_MAP[fcoType] || fcoType;

  return <TruncateText>{changeType}</TruncateText>;
};

const FeatureViewsTable: FC<FCOTypeTableProps> = ({ items, matchingTypes, fcoData }) => {
  const filteredItems = filterPlanDataByType(items, matchingTypes);
  const { workspace } = useParams();

  if (filteredItems.length === 0) {
    return <></>;
  }

  const columns: ExtendedTableColumn<StateUpdatePlanSummaryDiff & { id: string }>[] = [
    actionColumn,
    nameColumn(fcoData.featureViewsNamesMap, FCOType.FEATURE_VIEW, workspace ?? ''),
    changeColumn,
    {
      name: 'Type',
      columnWidth: 's',
      content: (item: StateUpdatePlanSummaryDiff) => <TypeColumn fcoType={item.fco_type} />,
      id: 'type',
    },
    {
      name: 'Materialization',
      id: 'materialization',
      content: (item: StateUpdatePlanSummaryDiff) => {
        if (
          item.type === 'DELETE' ||
          (item.fco_type !== 'Stream Feature View' && item.fco_type !== 'Batch Feature View')
        ) {
          return <>n/a</>;
        }

        const summaries = materializationTypesForFeatureView(item);
        if (summaries.length === 0) {
          return <>No Materialization</>;
        }

        return <TruncateText>{summaries.join(', ')}</TruncateText>;
      },
    },
  ];

  return (
    <>
      <FlexGroup direction="column" padding="0" gapSize="1">
        <FlexItem shrink={0} grow={1}>
          <Text element="h4">Feature Views & Tables</Text>
        </FlexItem>
        <FlexItem grow={1}>
          <Table
            items={filteredItems.map((item) => ({
              ...item,
              id: item.name ?? '',
            }))}
            columns={columns}
          />
        </FlexItem>
      </FlexGroup>
    </>
  );
};

const FeatureServicesTable: FC<FCOTypeTableProps> = ({ items, matchingTypes, fcoData }) => {
  const filteredItems = filterPlanDataByType(items, matchingTypes);
  const { workspace } = useParams();

  if (filteredItems.length === 0) {
    return <></>;
  }

  const columns: ExtendedTableColumn<StateUpdatePlanSummaryDiff & { id: string }>[] = [
    actionColumn,
    nameColumn(fcoData.featureServicesNamesMap, FCOType.FEATURE_SERVICE, workspace ?? ''),
    changeColumn,
    emptyColumn,
    emptyColumn,
  ];

  return (
    <>
      <FlexGroup direction="column" padding="0" gapSize="1">
        <FlexItem shrink={0}>
          <Text element="h4">Feature Services</Text>
        </FlexItem>
        <FlexItem grow={1}>
          <Table items={filteredItems.map((item) => ({ ...item, id: item.name ?? '' }))} columns={columns} />
        </FlexItem>
      </FlexGroup>
    </>
  );
};

const DataSourcesTable: FC<FCOTypeTableProps> = ({ items, matchingTypes, fcoData }) => {
  const filteredItems = filterPlanDataByType(items, matchingTypes);
  const { workspace } = useParams();

  if (filteredItems.length === 0) {
    return <></>;
  }

  const columns: ExtendedTableColumn<StateUpdatePlanSummaryDiff & { id: string }>[] = [
    actionColumn,
    nameColumn(fcoData.dataSourcesNamesMap, FCOType.DATA_SOURCE, workspace ?? ''),
    changeColumn,
    {
      name: 'Type',
      columnWidth: 'm',
      id: 'type',
      content: (item: StateUpdatePlanSummaryDiff) => {
        return <TypeColumn fcoType={item.fco_type} />;
      },
    },
    emptyColumn,
  ];

  return (
    <>
      <FlexGroup direction="column" padding="0" gapSize="1">
        <FlexItem shrink={0}>
          <Text element="h4">Data Sources</Text>
        </FlexItem>
        <FlexItem grow={1}>
          <Table items={filteredItems.map((item) => ({ ...item, id: item.name ?? '' }))} columns={columns} />
        </FlexItem>
      </FlexGroup>
    </>
  );
};

const EntitiesTable: FC<FCOTypeTableProps> = ({ items, matchingTypes, fcoData }) => {
  const filteredItems = filterPlanDataByType(items, matchingTypes);
  const { workspace } = useParams();

  if (filteredItems.length === 0) {
    return <></>;
  }

  const columns: ExtendedTableColumn<StateUpdatePlanSummaryDiff & { id: string }>[] = [
    actionColumn,
    nameColumn(fcoData.entitiesNamesMap, FCOType.ENTITY, workspace ?? ''),
    changeColumn,
    emptyColumn,
    emptyColumn,
  ];

  return (
    <FlexGroup direction="column" padding="0" gapSize="1">
      <FlexItem shrink={0}>
        <Text element="h4">Entities</Text>
      </FlexItem>
      <FlexItem grow={1}>
        <Table items={filteredItems.map((item) => ({ ...item, id: item.name ?? '' }))} columns={columns} />
      </FlexItem>
    </FlexGroup>
  );
};

const TransformationsTable: FC<FCOTypeTableProps> = ({ items, matchingTypes, fcoData }) => {
  const filteredItems = filterPlanDataByType(items, matchingTypes);
  const { workspace } = useParams();

  if (filteredItems.length === 0) {
    return <></>;
  }

  const columns: ExtendedTableColumn<StateUpdatePlanSummaryDiff & { id: string }>[] = [
    actionColumn,
    nameColumn(fcoData.transformationsNamesMap, FCOType.TRANSFORMATION, workspace ?? ''),
    changeColumn,
    emptyColumn,
    emptyColumn,
  ];

  return (
    <FlexGroup direction="column" padding="0" gapSize="1">
      <FlexItem shrink={0}>
        <Text element="h4">Transformations</Text>
      </FlexItem>
      <FlexItem grow={1}>
        <Table items={filteredItems.map((item) => ({ ...item, id: item.name ?? '' }))} columns={columns} />
      </FlexItem>
    </FlexGroup>
  );
};

const ApplySummary: FC<{ data: StateUpdatePlanSummary; fcoData: WorkspaceFCOContainer }> = ({ data, fcoData }) => {
  return (
    <>
      <FlexGroup direction="column" padding="2" gapSize="4">
        <FlexItem shrink={0} grow={0}>
          <FeatureViewsTable
            items={data.diff_items ?? []}
            matchingTypes={[
              'Feature Table',
              'Realtime Feature View',
              'Batch Feature View',
              'Stream Feature View',
              'Stream Feature View with Push Source',
              'Realtime Feature View',
            ]}
            fcoData={fcoData}
          />
        </FlexItem>
        <FlexItem shrink={0} grow={0}>
          <FeatureServicesTable items={data.diff_items ?? []} matchingTypes={['Feature Service']} fcoData={fcoData} />
        </FlexItem>
        <FlexItem shrink={0} grow={0}>
          <DataSourcesTable
            items={data.diff_items ?? []}
            matchingTypes={['Stream Data Source', 'Batch Data Source', 'Push Source']}
            fcoData={fcoData}
          />
        </FlexItem>
        <FlexItem shrink={0} grow={0}>
          <EntitiesTable items={data.diff_items ?? []} matchingTypes={['Entity']} fcoData={fcoData} />
        </FlexItem>
        <FlexItem shrink={0} grow={0}>
          <TransformationsTable items={data.diff_items ?? []} matchingTypes={['Transformation']} fcoData={fcoData} />
        </FlexItem>
      </FlexGroup>
    </>
  );
};

const CLIOutput: FC<{ commitId: string }> = ({ commitId }) => {
  const { workspace } = useUserSettings();

  const { data, isLoading } = useGetQueryStateUpdateResponse(commitId ?? '', workspace ?? '');

  if (isLoading) {
    return (
      <FlexGroup direction="column" alignItems="center">
        <FlexItem>
          <Loading />
        </FlexItem>
      </FlexGroup>
    );
  }

  return (
    <>
      {data?.successful_plan_output?.string_output && (
        <Code code={data?.successful_plan_output?.string_output} language="bash" />
      )}
    </>
  );
};

const ApplyLogRightSide: FC<{ data: StateUpdatePlanSummary }> = ({ data }) => {
  const commitId = useLocation().pathname.split('/').reverse()[0];
  const { workspace } = useUserSettings();

  const { data: fcoData, isLoading: fcoIsLoading } = useGetFcos(workspace ?? '', {});

  const [tab, setTab] = useState('Apply Details');

  if (fcoIsLoading) {
    return (
      <FlexGroup direction="column" alignItems="center">
        <FlexItem>
          <Loading />
        </FlexItem>
      </FlexGroup>
    );
  }

  return (
    <FlexGroup direction="column" gapSize="0">
      <Tabs selectedTab={tab} items={[{ label: 'Apply Details' }, { label: 'CLI Output' }]} setSelectedTab={setTab} />

      <FlexItem>
        {tab === 'Apply Details' && <ApplySummary data={data} fcoData={fcoData} />}
        {tab === 'CLI Output' && <CLIOutput commitId={commitId} />}
      </FlexItem>
    </FlexGroup>
  );
};

const ObjectChanges: FC<{ data: StateUpdatePlanSummary }> = ({ data }) => {
  const summary = processDiffItems(data.diff_items);

  return (
    <>
      <Text element="h5">Object Changes</Text>
      {summary.map((summaryItem) => {
        const items = summaryItem.items_by_status;

        const columns: ExtendedTableColumn<SingleDiffSummaryLine & { id: string }>[] = [
          {
            name: summaryItem.fco_type,
            id: 'fco_type',
            content: (itemSummary: SingleDiffSummaryLine) => {
              return <ActionBadge type={itemSummary.status} />;
            },
          },
          {
            name: <></>,
            id: 'count',
            content: (itemSummary: SingleDiffSummaryLine) => {
              return itemSummary.items.length;
            },
            columnWidth: 's',
          },
        ];
        return <Table items={items.map((item) => ({ ...item, id: item.items[0].name ?? '' }))} columns={columns} />;
      })}
    </>
  );
};

const MetadataCard: FC<{ data: StateUpdatePlanSummary }> = ({ data }) => {
  const metadataItems = [
    {
      title: 'Created By',
      content: (
        <FlexGroup padding="0">
          <FlexItem grow={0}>
            <Avatar name={data.created_by} />
          </FlexItem>
          <FlexItem>{data.created_by}</FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Created At',
      content: TectonDateTimeFormat(moment(data.applied_at)),
    },
    {
      title: 'Applied By',
      content: (
        <FlexGroup padding="0">
          <FlexItem grow={0}>
            <Avatar name={data.applied_by} />
          </FlexItem>
          <FlexItem>{data.applied_by}</FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Workspace',
      content: <TruncateText>{data.workspace}</TruncateText>,
    },
    {
      title: 'SDK Version',
      content: <>{data.sdk_version}</>,
    },
  ];

  return (
    <Card title="Metadata">
      <>
        <FlexGroup direction="column">
          <FlexItem>
            <KeyValueList items={metadataItems} />
          </FlexItem>
          <FlexItem>
            <ObjectChanges data={data} />
          </FlexItem>
        </FlexGroup>
      </>
    </Card>
  );
};

const ApplyLogCommitDetailsCallout: FC<{ data?: StateUpdatePlanSummaryType; summaries: React.ReactNode[] }> = ({
  data,
  summaries,
}) => {
  if (summaries.length === 0) {
    return <></>;
  }

  const concattedSummary = summaries.reduce<React.ReactNode[]>((memo, entry, i, array) => {
    if (array.length === 1) {
      return [entry];
    } else {
      const separator = <span key={`separator-${i}`}>{i === array.length - 2 ? <>&nbsp;and</> : <>,</>}</span>;

      memo.push(entry);
      if (i < array.length - 1) {
        memo.push(separator);
      }

      return memo;
    }
  }, []);

  if (data?.hasApplied) {
    return (
      <Callout
        variant="success"
        title="Note"
        icon
        content={<>Tecton created&nbsp;{concattedSummary}&nbsp;when this plan was applied.</>}
        singleLine
      />
    );
  }

  // Integration tests are only active if at least one of the StateUpdateDiff items includes materialization_info
  if (data?.plan?.diff_items?.find((item) => item.materialization_info)) {
    return (
      <Callout
        variant="info"
        title="Note"
        icon
        content={
          <div>
            Tecton is running integration tests for this Tecton Apply request. If successful, it will create
            {concattedSummary}. To view the integration test jobs, visit the&nbsp;
            <Link url={`../../jobs`} name={'Jobs Page'} />.
          </div>
        }
      />
    );
  }

  return <></>;
};

const ApplyLogDetails: FC<{ data?: StateUpdatePlanSummaryType }> = ({ data }) => {
  const { batchMaterialization, streamMaterialization, backfillJobs } = processMaterializationInfo(
    data?.plan?.diff_items ?? []
  );

  const summaries: React.ReactNode[] = [];

  let backfillCount = 0;
  if (backfillJobs.length > 0) {
    backfillCount = backfillJobs.reduce<number>((memo, current) => {
      return memo + (current?.number_of_jobs ?? 0);
    }, 0);

    summaries.push(
      <span key="backfill">
        {' '}
        <>{backfillCount}</> backfill job
        {backfillCount > 1 ? 's' : ''}
      </span>
    );
  }

  if (batchMaterialization.length > 0) {
    summaries.push(
      <span key="batch">
        {' '}
        <>&nbsp;{batchMaterialization.length}</> recurring batch job
        {batchMaterialization.length > 1 ? 's' : ''}
      </span>
    );
  }

  if (streamMaterialization.length > 0) {
    summaries.push(
      <span key="stream">
        {' '}
        <>&nbsp;{streamMaterialization.length}</> recurring stream job
        {streamMaterialization.length > 1 ? 's' : ''}
      </span>
    );
  }

  if (!data?.plan) {
    return <></>;
  }

  return (
    <>
      {summaries.length > 0 ? (
        <>
          <FlexGroup direction="column" padding="0" gapSize="1">
            <FlexItem shrink={1} grow={0}>
              <FlexGroup direction="column" gapSize="3" padding="0">
                <FlexItem grow={1} shrink={0}>
                  <ApplyLogCommitDetailsCallout data={data} summaries={summaries} />
                </FlexItem>
                <FlexItem grow={1} shrink={1}>
                  <AsymmetricColumnLayout
                    left={<MetadataCard data={data?.plan} />}
                    right={<ApplyLogRightSide data={data?.plan} />}
                  />
                </FlexItem>
              </FlexGroup>
            </FlexItem>
          </FlexGroup>
        </>
      ) : (
        <>
          <AsymmetricColumnLayout
            ratio={[1, 2]}
            left={<MetadataCard data={data?.plan} />}
            right={<ApplyLogRightSide data={data?.plan} />}
          />
        </>
      )}
    </>
  );
};

const ApplyLogCommitDetails: FC = () => {
  const { workspaceDetails } = useUserSettings();

  const commitId = useLocation().pathname.split('/').reverse()[0];

  const { data, isLoading } = useGetStateUpdatePlanSummary(commitId);

  if (isLoading) {
    return (
      <FlexGroup direction="column" alignItems="center">
        <FlexItem>
          <Loading />
        </FlexItem>
      </FlexGroup>
    );
  }

  return (
    <ViewLayout
      header={{
        breadcrumbs: {
          root: {
            label: workspaceDetails?.name ?? 'Workspace',
            url: `repo/${workspaceDetails?.name}`,
          },
          items: [{ label: 'Apply Log', url: `/repo/${workspaceDetails?.name}/home/apply-log` }, { label: commitId }],
        },
        title: 'Commit Details',
      }}
      content={<ApplyLogDetails data={data} />}
    />
  );
};

export default ApplyLogCommitDetails;
